/**
 * Cart Drawer - Slake Brewing
 * Handles AJAX add-to-cart and drawer functionality
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = this.drawer?.querySelector('.cart-drawer-overlay');
    this.container = this.drawer?.querySelector('.cart-drawer-container');
    this.cartIcon = document.querySelector('.cart a');

    // Track the element that opened the drawer for focus restoration
    this.triggerElement = null;

    // Debounce timer for quantity updates
    this.quantityDebounceTimer = null;
    this.isUpdating = false;

    // Track whether user is using keyboard navigation
    this.isKeyboardUser = false;

    if (!this.drawer) return;

    this.init();
  }

  init() {
    // Detect keyboard vs mouse users for focus styling
    // Add class on Tab, remove on mouse click
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.body.classList.add('user-is-tabbing');
        this.isKeyboardUser = true;
      }
    });

    document.addEventListener('mousedown', () => {
      document.body.classList.remove('user-is-tabbing');
      this.isKeyboardUser = false;
    });

    // Escape key to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });

    // Focus trap - keep Tab within drawer when open
    this.drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.handleTabKey(e);
      }
    });

    // Cart icon click - open drawer (only the actual cart link, not logo)
    const headerCartLink = document.querySelector('header .cart a');
    if (headerCartLink) {
      headerCartLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.triggerElement = e.currentTarget;
        this.open();
      });
    }

    // Single delegated click handler for all drawer interactions.
    // This works even after refreshCart() replaces the innerHTML,
    // because the listener is on the parent drawer element.
    this.drawer.addEventListener('click', (e) => {
      // Close button or overlay
      if (e.target.closest('[data-cart-drawer-close]')) {
        this.close();
        return;
      }

      // Prevent rapid quantity clicks while updating
      if (this.isUpdating) return;

      // Decrease quantity
      if (e.target.closest('[data-cart-item-decrease]')) {
        const key = e.target.closest('[data-cart-item-decrease]').dataset.cartItemDecrease;
        this.debouncedUpdateQuantity(key, -1);
      }

      // Increase quantity
      if (e.target.closest('[data-cart-item-increase]')) {
        const key = e.target.closest('[data-cart-item-increase]').dataset.cartItemIncrease;
        this.debouncedUpdateQuantity(key, 1);
      }

      // Remove item
      if (e.target.closest('[data-cart-item-remove]')) {
        const key = e.target.closest('[data-cart-item-remove]').dataset.cartItemRemove;
        this.removeItem(key);
      }
    });

    // Quantity input change
    this.drawer.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-item-qty]')) {
        const key = e.target.dataset.cartItemQty;
        const newQty = parseInt(e.target.value, 10);
        this.setQuantity(key, newQty);
      }
    });

    // Intercept add-to-cart forms
    this.interceptAddToCartForms();
  }

  /**
   * Handle Tab key for focus trapping
   */
  handleTabKey(e) {
    const focusableElements = this.drawer.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab: if on first element, go to last
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: if on last element, go to first
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Debounced quantity update to prevent rapid-fire requests
   */
  debouncedUpdateQuantity(key, change) {
    clearTimeout(this.quantityDebounceTimer);
    this.quantityDebounceTimer = setTimeout(() => {
      this.updateQuantity(key, change);
    }, 150);
  }

  interceptAddToCartForms() {
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action="/cart/add"]')) {
        e.preventDefault();
        this.triggerElement = e.submitter || e.target.querySelector('[type="submit"]');
        this.addToCart(e.target);
      }
    });
  }

  async addToCart(form) {
    const formData = new FormData(form);

    // Disable submit button during request
    const submitBtn = form.querySelector('[type="submit"], .btn-add-to-cart');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await response.json();
      await this.refreshCart();
      this.open();
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showNotification('Failed to add to cart', 'error');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  async updateQuantity(key, change) {
    const input = this.drawer.querySelector(`[data-cart-item-qty="${key}"]`);
    if (!input) return;

    const currentQty = parseInt(input.value, 10);
    const newQty = Math.max(0, currentQty + change);

    await this.setQuantity(key, newQty);
  }

  async setQuantity(key, quantity) {
    // Show loading state and prevent further updates
    this.isUpdating = true;
    const body = this.drawer.querySelector('.cart-drawer-body');
    if (body) body.classList.add('is-loading');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      await this.refreshCart();
    } catch (error) {
      console.error('Update quantity error:', error);
      this.showNotification('Failed to update quantity', 'error');
    } finally {
      // Remove loading state
      this.isUpdating = false;
      if (body) body.classList.remove('is-loading');
    }
  }

  async removeItem(key) {
    await this.setQuantity(key, 0);
  }

  async refreshCart() {
    try {
      const response = await fetch('/?sections=cart-drawer');
      const data = await response.json();

      if (data['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newDrawerContent = doc.querySelector('.cart-drawer-container');

        if (newDrawerContent && this.container) {
          this.container.innerHTML = newDrawerContent.innerHTML;
        }
      }

      // Update cart count in header
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error('Refresh cart error:', error);
    }
  }

  updateCartCount(count) {
    // Update drawer count
    const drawerCounts = document.querySelectorAll('.cart-drawer-count');
    drawerCounts.forEach(el => {
      el.textContent = count;
    });

    // Update header cart count (just the number, not the container)
    const headerCartCount = document.querySelector('header .cart .count');
    if (headerCartCount) {
      headerCartCount.textContent = count;
    }
  }

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');

    // Trigger animation
    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
    });

    // Move focus to the close button only for keyboard users
    // Programmatic focus on Safari can interfere with mouse clicks
    if (this.isKeyboardUser) {
      setTimeout(() => {
        const closeBtn = this.drawer.querySelector('.cart-drawer-close');
        if (closeBtn) closeBtn.focus();
      }, 100);
    }
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');

    // Wait for animation, then update aria and restore focus
    setTimeout(() => {
      this.drawer.setAttribute('aria-hidden', 'true');

      // Only restore focus for keyboard users - prevents the
      // visible focus ring on the cart icon after mouse close
      if (this.isKeyboardUser && this.triggerElement && typeof this.triggerElement.focus === 'function') {
        this.triggerElement.focus();
      }
    }, 300);
  }

  showNotification(message, type = 'error') {
    // Create toast notification
    const existing = document.querySelector('.cart-notification');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = `cart-notification cart-notification--${type}`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;

    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Remove after 3 seconds
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartDrawer());
} else {
  new CartDrawer();
}
